import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, catchError, map, withLatestFrom, concatMap, tap } from 'rxjs/operators';
import * as userActions from '../actions/user.actions';
import * as errorActions from '../actions/error.actions';
import * as profileActions from '../actions/profile.actions';
import * as userSelectors from '../selectors/user.selector';
import { of } from 'rxjs';
import { UserService, ScdService } from '@app-root-services';
import { IUser, IError } from '@app-root-interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { appReducer } from '@app-root-store';
import { documentActions } from '../actions';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';

@Injectable()
export class UserEffects {
    private store$ = inject<Store<appReducer.AppState>>(Store);
    private actions$ = inject(Actions);
    private userService = inject(UserService);
    private scdService = inject(ScdService);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private toastNotificationService = inject(SiToastNotificationService);

    getUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.getUser),
            switchMap(({ gid }) =>
                this.userService.getUser(gid).pipe(
                    switchMap((user: { user: IUser; isAdmin: boolean }) => [
                        userActions.getUserSuccess({
                            user: user.user,
                            isAdmin: user.isAdmin
                        }),
                        profileActions.getUserProfiles({ user: user.user })
                    ]),
                    catchError((err: HttpErrorResponse) => {
                        if (err.status === 404) {
                            return of(userActions.getScdUserForInitialUserCreate({ gid }));
                        } else {
                            const error: IError = {
                                obj: err,
                                isCritical: true,
                                text: 'user-error-get'
                            };
                            return of(errorActions.addError({ error }));
                        }
                    })
                )
            )
        )
    );

    getScdUserForInitialUserCreate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.getScdUserForInitialUserCreate),
            switchMap(({ gid }) =>
                this.scdService.getScdUser(gid).pipe(
                    map((scdUser) => userActions.createInitialUser({ scdUser })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: true,
                            text: 'user-error-get-scd'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    createInitialUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.createInitialUser),
            switchMap(({ scdUser }) =>
                this.userService.createInitialUser(scdUser).pipe(
                    switchMap((user) => [
                        userActions.createInitialUserSuccess({ user, isAdmin: false }),
                        profileActions.getUserProfiles({ user: user })
                    ]),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: true,
                            text: 'user-error-save'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    toggleFavouriteFromUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.toggleFavouriteFromUser),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            concatMap(([{ documentId }, user]) =>
                this.userService.toogleFavourites(user, documentId).pipe(
                    map((updatedUser) => userActions.toggleFavouriteFromUserSuccess({ user: updatedUser })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'document-error-favorites-add'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    addToRecentlySeen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.addRecentlySeenToUser),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            concatMap(([{ documentId }, user]) =>
                this.userService.addToRecentlySeen(user, documentId).pipe(
                    map((updatedUser) => userActions.addRecentlySeenToUserSuccess({ user: updatedUser })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'document-error-lastused-add'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    updateUserSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.updateUserSettings),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            concatMap(([{ settings }, user]) => {
                return this.userService.updateUserSettings(user, settings).pipe(
                    map((updatedUser) => userActions.updateUserSettingsSuccess({ user: updatedUser })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'user-error-settings-update'
                        };
                        return of(errorActions.addError({ error }));
                    })
                );
            })
        )
    );

    setIsAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.setIsAdmin),
            tap(({ isAdmin }) => {
                if (isAdmin) {
                    this.router.navigate(['main', 'all']);
                } else {
                    this.router.navigate(['main', 'my-unit']);
                }
                sessionStorage.setItem('isAdmin', `${isAdmin}`);
            }),
            map(({ isAdmin }) => userActions.setIsAdminSuccess({ isAdmin }))
        )
    );

    setIsAdminSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.setIsAdminSuccess),
            map(() => documentActions.getDrive())
        )
    );

    updateUserSettingsSuccessToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(userActions.updateUserSettingsSuccess),
                tap(() =>
                    this.toastNotificationService.showToastNotification({
                        state: 'success',
                        title: this.translateService.instant('success'),
                        message: this.translateService.instant('settings-save-success'),
                        timeout: 8000
                    })
                )
            ),
        { dispatch: false }
    );
}
